import { useMemo } from "react";
import { useGuestPoint } from "@/core/point/hooks";
import type { MenuCategory, MenuProduct, MenuProductTag } from "@/types";
import {
  useGuestCategories,
  useGuestProductTags,
  useGuestProducts,
  useGuestVariantProperties,
} from "./guest-hooks";
import { useGuestImageUrls } from "./images";
import { mapGuestCategory, mapGuestProduct } from "./map";
import type { MapContext } from "./map";

/** Стабильная ссылка: новый [] на каждый рендер ронял бы мемоизацию. */
const EMPTY_IMAGE_IDS: readonly (string | null)[] = [];

/**
 * Считается ли меню загруженным.
 *
 * Правило спеки §5.3, вынесено чистой функцией, чтобы его нельзя было
 * изменить незаметно: спиннер держат только точка, категории и товары.
 * Картинки, теги и свойства вариантов дозагружаются — их отсутствие даёт
 * карточку без обложки, а не спиннер поверх готового меню.
 */
export function isMenuLoading(flags: {
  point: boolean;
  categories: boolean;
  products: boolean;
}): boolean {
  return flags.point || flags.categories || flags.products;
}

/**
 * Ошибка загрузки меню.
 *
 * Провал словарей ошибкой не считается: меню без имён тегов читаемо.
 * 404 точки сюда не доходит — это отдельное состояние, его владелец
 * DataLoader. Пустой ответ ошибкой тоже не является: «бренд, у которого не
 * заведено ни одного меню, — тоже пустой список, а не отказ» (схема).
 */
export function menuError(errors: {
  categories: Error | null;
  products: Error | null;
}): Error | null {
  return errors.categories ?? errors.products ?? null;
}

/**
 * Композиция гостевого меню: пять запросов, join картинок и тегов,
 * отображение в UI-типы.
 *
 * Везде isLoading, а не isPending: у отключённого запроса isPending в
 * TanStack v5 не сбрасывается никогда, и при пустом pointId всё залипло бы
 * в загрузке.
 */
export function useMenuData(pointId: string | null) {
  const point = useGuestPoint(pointId);
  const categoriesQuery = useGuestCategories(pointId);
  const productsQuery = useGuestProducts(pointId);
  const tagsQuery = useGuestProductTags(pointId);
  const variantPropertiesQuery = useGuestVariantProperties(pointId);

  const categoriesData = categoriesQuery.data;
  const productsData = productsQuery.data;
  // Товары приезжают постранично (круг 7), и productsData становится
  // непустой уже после ПЕРВОЙ страницы. Полнота набора id — это конец
  // дочитки, а её и означает isLoading хука товаров.
  const productsSettled = !productsQuery.isLoading;

  const imageIds = useMemo(() => {
    // Ждём ОБА списка ЦЕЛИКОМ, прежде чем просить картинки. Иначе набор id
    // достраивается по мере ответов, и каждое промежуточное состояние —
    // это свой ключ кэша и свой запрос: смоук на деве показал четыре
    // обращения к /guest/images там, где хватает одного.
    //
    // С пагинацией цена этой ошибки перестала быть линейной. useGuestImageUrls
    // СОРТИРУЕТ уникальные id перед нарезкой на пачки по 100, поэтому новые
    // id перекраивают разбиение целиком — ключ меняется у ВСЕХ пачек сразу, а
    // не только у последней. Меню в P страниц стоило бы ≈P(P+1)/2 обращений
    // к /guest/images вместо P: десять страниц — пятьдесят пять запросов,
    // и каждый прилёт перестраивал бы полный маппинг меню.
    if (!categoriesData || !productsData || !productsSettled) {
      return EMPTY_IMAGE_IDS;
    }

    const ids: (string | null)[] = [];
    for (const category of categoriesData) {
      ids.push(category.imageBind);
    }
    for (const item of productsData) {
      if (item.type === "productVariantGroup") {
        for (const variant of item.productVariants) {
          ids.push(...variant.imageBinds);
        }
      } else {
        ids.push(...item.imageBinds);
      }
    }
    return ids;
    // data стабильна по ссылке благодаря structural sharing TanStack —
    // без этого imageIds пересоздавался бы и ронял мемоизацию ниже.
  }, [categoriesData, productsData, productsSettled]);

  const imageUrls = useGuestImageUrls(pointId, imageIds);

  const tags = useMemo<MenuProductTag[]>(
    () => (tagsQuery.data ?? []).map((tag) => ({ id: tag.id, name: tag.name })),
    [tagsQuery.data],
  );

  const ctx = useMemo<MapContext>(() => {
    const tagsById: Record<string, MenuProductTag> = {};
    for (const tag of tags) tagsById[tag.id] = tag;
    return { tagsById, imageUrls };
  }, [tags, imageUrls]);

  const categories = useMemo<MenuCategory[]>(
    () =>
      (categoriesQuery.data ?? []).map((category) =>
        mapGuestCategory(category, imageUrls),
      ),
    [categoriesQuery.data, imageUrls],
  );

  const products = useMemo<MenuProduct[]>(() => {
    const mapped: MenuProduct[] = [];
    for (const item of productsQuery.data ?? []) {
      const product = mapGuestProduct(item, ctx);
      // null = группа без единого доступного в этой точке варианта:
      // рисовать её нечем (спека §3.4).
      if (product) mapped.push(product);
    }
    return mapped;
  }, [productsQuery.data, ctx]);

  const variantProperties = variantPropertiesQuery.data;
  const variantPropertiesLoading = variantPropertiesQuery.isLoading;
  const pointLoading = point.isLoading;
  const categoriesLoading = categoriesQuery.isLoading;
  const productsLoading = productsQuery.isLoading;
  const categoriesError = categoriesQuery.error as Error | null;
  const productsError = productsQuery.error as Error | null;
  const productsPartial = productsQuery.isPartial;

  return useMemo(
    () => ({
      categories,
      products,
      tags,
      variantProperties: variantProperties ?? [],
      // Отдельным флагом, а не частью isLoading: словарь свойств спиннер
      // над меню не держит (правило §5.3), но карточка группы вариантов
      // без него нарисует товар БЕЗ селектора вариантов — и «в корзину»
      // уедет группа с пустым выбором. Гейтить им надо именно карточку.
      isVariantPropertiesLoading: variantPropertiesLoading,
      isLoading: isMenuLoading({
        point: pointLoading,
        categories: categoriesLoading,
        products: productsLoading,
      }),
      error: menuError({
        categories: categoriesError,
        products: productsError,
      }),
      // Только товары: категории приезжают одной выдачей, обхода у них нет.
      isPartial: productsPartial,
    }),
    [
      categories,
      products,
      tags,
      variantProperties,
      variantPropertiesLoading,
      pointLoading,
      categoriesLoading,
      productsLoading,
      categoriesError,
      productsError,
      productsPartial,
    ],
  );
}
